import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * One-tap login via magic link sent by WhatsApp.
 * URL: /auth/magic?token=xxx
 * Exchanges the token for auth cookies + a target path (deep-link).
 */
export default function MagicAuth() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const token = sp.get("token") || "";
  const [status, setStatus] = useState(token ? "loading" : "missing");
  const [error, setError] = useState("");
  const [target, setTarget] = useState("/driver");
  const firedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    // Guard against StrictMode double-invoke: only exchange once per token.
    // Do NOT use a "cancel" flag because StrictMode's cleanup would abort our
    // successful promise before navigation.
    if (firedRef.current) return;
    firedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/magic/exchange", { token });
        setTarget(data.target_path || "/driver");
        try { await refresh(); } catch { /* ignore */ }
        setStatus("ok");
        setTimeout(() => nav(data.target_path || "/driver", { replace: true }), 400);
      } catch (e) {
        setStatus("error");
        setError(e?.response?.data?.detail || "Link inválido ou expirado.");
      }
    })();
  }, [token, refresh, nav]);

  if (status === "missing") return <Navigate to="/login" replace />;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#0a0f1e", color: "#fff" }}
      data-testid="magic-auth-page"
    >
      <div className="w-full max-w-sm text-center">
        <div className="text-2xl font-display tracking-wide mb-6" style={{ color: "#f59e0b" }}>
          DI<span style={{ color: "#fff" }}>TOURS</span>
        </div>

        {status === "loading" && (
          <>
            <div className="animate-pulse text-xl font-display mb-2" data-testid="magic-auth-loading">
              A autenticar…
            </div>
            <div className="text-sm opacity-70">Aguarde um instante.</div>
          </>
        )}

        {status === "ok" && (
          <>
            <div className="text-xl font-display mb-2" data-testid="magic-auth-success">
              ✓ Autenticado
            </div>
            <div className="text-sm opacity-70">A redireccionar para {target}…</div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="text-xl font-display mb-3" style={{ color: "#f87171" }} data-testid="magic-auth-error">
              Não foi possível autenticar
            </div>
            <div className="text-sm opacity-80 mb-6">{error}</div>
            <button
              className="w-full h-11 border border-[#f59e0b] text-[#f59e0b] uppercase text-xs tracking-wide"
              onClick={() => nav("/login", { replace: true })}
              data-testid="magic-auth-goto-login"
            >
              Entrar manualmente
            </button>
          </>
        )}
      </div>
    </div>
  );
}
